import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Menu, X } from 'lucide-react';
import { usePermission } from '../auth/PermissionGate';
import { LeadSignalGuide } from '../leads/LeadSignalGuide';
import { getModuleAccent } from './moduleAccents';

/**
 * Secondary Header — Polished SaaS sub-nav.
 *
 * Visual language:
 *   • 3px module-color strip up top (wayfinding, no text).
 *   • A small "hanging notch" of module color drops from the strip
 *     directly above the active tab, tying the two visually.
 *   • Active tab = gradient module color pill w/ inset highlight,
 *     glow shadow, tiny white marker dot inside.
 *   • Inactive tabs = quiet text pills. Hover reveals a slim colored
 *     underline (module accent) that slides in.
 *   • Bar itself has a subtle top-to-bottom gradient (white → slate-50/40)
 *     and a soft inset bottom edge for depth (not a harsh border).
 *   • Faint hairline glow beneath the color strip.
 */
export function SecondaryHeader({ tabs }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const can = usePermission();
  const navRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false });

  const visibleTabs = tabs ? tabs.filter(tab => {
    if (!tab.requirePerm) return true;
    return can(tab.requirePerm[0], tab.requirePerm[1]);
  }) : [];

  const isActive = useCallback((tab) => {
    const path = tab.path;
    if (path.includes('?')) {
      const [basePath, query] = path.split('?');
      if (location.pathname !== basePath) return false;
      if (location.search.includes(query)) return true;
      // First / default tab is active when no matching query is present
      if (tab.default && !location.search) return true;
      return false;
    }
    return location.pathname === path;
  }, [location.pathname, location.search]);

  // Measure active tab position to place the "hanging notch"
  useEffect(() => {
    if (!navRef.current) return;
    const activeEl = navRef.current.querySelector('[data-sec-active="true"]');
    if (!activeEl) {
      setIndicator({ left: 0, width: 0, ready: false });
      return;
    }
    const navRect = navRef.current.getBoundingClientRect();
    const activeRect = activeEl.getBoundingClientRect();
    setIndicator({
      left: activeRect.left - navRect.left + activeRect.width / 2,
      width: 24,
      ready: true,
    });
  }, [location.pathname, location.search, visibleTabs.length, collapsed]);

  if (!tabs || tabs.length === 0) return null;
  if (visibleTabs.length === 0) return null;

  const activeTab = visibleTabs.find(t => isActive(t));
  const accent = getModuleAccent(location.pathname);

  return (
    <div
      className="sticky top-[60px] z-40 bg-gradient-to-b from-white via-white to-slate-50/60 shadow-[inset_0_-1px_0_0_rgba(15,23,42,0.06)]"
      data-testid="secondary-header"
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      {/* ── Module wayfinding strip ──────────────────────────── */}
      <div className="relative">
        <div
          className={cn('h-[3px] w-full', accent.tab.stripBg)}
          aria-hidden
          data-testid="module-strip"
        />
        {/* Faint drop-glow under the strip */}
        <div
          className={cn('absolute inset-x-0 top-[3px] h-2 opacity-40 blur-[6px] pointer-events-none', accent.tab.stripBg)}
          aria-hidden
        />
      </div>

      {/* ── Nav row (nav is the positioning parent for the notch) ── */}
      <div className="px-5 flex items-center h-12 gap-3">
        {/* Mobile hamburger */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 rounded-md hover:bg-slate-100 transition-colors lg:hidden"
          data-testid="secondary-menu-toggle"
        >
          {collapsed
            ? <X className="h-4 w-4 text-slate-500" strokeWidth={2} />
            : <Menu className="h-4 w-4 text-slate-500" strokeWidth={2} />
          }
        </button>

        {/* Segmented tab group */}
        <nav
          ref={navRef}
          className={cn(
            'relative inline-flex items-center gap-1 flex-1 overflow-x-auto scrollbar-hide',
            'max-lg:hidden',
            collapsed && 'max-lg:flex'
          )}
          data-testid="secondary-nav"
        >
          {/* Hanging notch — a small module-colored pin sitting on the color strip above the active tab */}
          {indicator.ready && (
            <span
              aria-hidden
              className={cn(
                'pointer-events-none absolute -top-[7px] h-[4px] rounded-b-md transition-all duration-300 ease-out',
                accent.tab.stripBg
              )}
              style={{
                left: `${indicator.left - indicator.width / 2}px`,
                width: `${indicator.width}px`,
                boxShadow: '0 2px 6px -1px rgba(0,0,0,0.18)',
              }}
              data-testid="active-tab-notch"
            />
          )}

          {visibleTabs.map((tab) => {
            const active = isActive(tab);
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                data-testid={`sub-nav-${tab.id}`}
                data-sec-active={active ? 'true' : undefined}
                className={cn(
                  'group relative inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] whitespace-nowrap transition-all duration-200 tracking-tight',
                  active
                    ? cn(
                        'text-white font-semibold animate-[fadeInScale_240ms_ease-out]',
                        accent.tab.solidBg,
                        accent.tab.solidHover,
                        accent.tab.solidShadow
                      )
                    : 'text-slate-600 hover:text-slate-900 font-medium'
                )}
              >
                {/* Small inner marker dot for active tab (adds premium detail) */}
                {active && (
                  <span
                    className="h-1 w-1 rounded-full bg-white/70 shadow-[0_0_4px_rgba(255,255,255,0.6)]"
                    aria-hidden
                  />
                )}
                {Icon && !active && (
                  <Icon
                    size={13}
                    strokeWidth={1.75}
                    className="text-slate-400 group-hover:text-slate-700 transition-colors"
                  />
                )}
                <span>{tab.label}</span>
                {tab.badge && (
                  <span className={cn(
                    'ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[16px] px-1.5 text-[9.5px] rounded font-bold tabular-nums',
                    active
                      ? 'bg-white/25 text-white ring-1 ring-white/40'
                      : 'bg-slate-200 text-slate-600'
                  )}>
                    {tab.badge}
                  </span>
                )}

                {/* Slim underline slides in on inactive hover */}
                {!active && (
                  <span
                    aria-hidden
                    className={cn(
                      'absolute left-3 right-3 bottom-0.5 h-[2px] rounded-full origin-center scale-x-0 group-hover:scale-x-100 transition-transform duration-200 ease-out',
                      accent.tab.hoverUnderline
                    )}
                  />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Mobile compact label */}
        {!collapsed && activeTab && (
          <span className="lg:hidden text-[12.5px] font-semibold text-slate-900">{activeTab.label}</span>
        )}

        {/* Page-level action slot — pages portal their controls (Date range, Refresh, etc.) here */}
        <div
          id="secondary-header-actions"
          className="flex items-center gap-1.5 shrink-0"
          data-testid="secondary-header-actions-slot"
        />

        {/* Right-side page-specific action */}
        {location.pathname.startsWith('/leads') && (
          <div className="flex items-center shrink-0 pl-2 border-l border-slate-200/70">
            <LeadSignalGuide />
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0.35; transform: scale(0.94); }
          to   { opacity: 1;    transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

export default SecondaryHeader;
